import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  WorkflowDetailData,
  initialWorkflowDetailData,
} from "../../types/Workflow";
import { ListData } from "../../types/List";

interface ComponentProps {
  onSubmit: (data: Partial<WorkflowDetailData>) => void;
  onClose: () => void;
  initialData?: Partial<WorkflowDetailData>;
  roleData: ListData[];
  statusData: ListData[];
}

const WorkflowDetailForm: React.FC<ComponentProps> = ({
  onSubmit,
  onClose,
  initialData,
  roleData,
  statusData,
}) => {
  const [inputData, setInputData] = useState<Partial<WorkflowDetailData>>(
    initialData || initialWorkflowDetailData
  );
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (initialData) {
      setInputData(initialData);
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setInputData((prev: Partial<WorkflowDetailData>) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!inputData.workflowId) newErrors.workflowId = "Worklow Id is required";
    if (!inputData.statusId) newErrors.statusId = "Status is required";
    if (!inputData.taskLevel) newErrors.taskLevel = "Task level is required";
    if (!inputData.location) newErrors.location = "Local is required";

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    onSubmit(inputData);
  };
  const handleSwitch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    setInputData((prevData) => ({
      ...prevData,
      [name]: checked,
    }));
  };
  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
      <Box
        display="grid"
        gridTemplateColumns={isMobile ? "1fr" : "repeat(1, 1fr)"}
        gap={2}
      >
        <Box>
          <TextField
            name="taskLevel"
            label="Task Level"
            value={inputData.taskLevel}
            onChange={handleChange}
            fullWidth
            margin="normal"
            error={!!errors.taskLevel}
            helperText={errors.taskLevel}
            InputProps={{
              readOnly: true,
            }}
            sx={{
              "& .MuiInputBase-input.Mui-disabled, & .MuiInputBase-input[readonly]":
                {
                  backgroundColor: "#dad6d6df",
                  color: "#555",
                },
            }}
          />

          <TextField
            select
            name="roleId"
            label="Role"
            value={inputData.roleId}
            onChange={handleChange}
            fullWidth
            margin="normal"
            error={!!errors.roleId}
            helperText={errors.roleId}
          >
            <MenuItem key={0} value={0}>
              Please select
            </MenuItem>
            {roleData.map((data) => (
              <MenuItem key={data.id} value={data.id}>
                {data.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            name="statusId"
            label="Status"
            value={inputData.statusId}
            onChange={handleChange}
            fullWidth
            margin="normal"
            error={!!errors.statusId}
            helperText={errors.statusId}
          >
            <MenuItem key={0} value={0}>
              Please select
            </MenuItem>
            {statusData.map((data) => (
              <MenuItem key={data.id} value={data.id}>
                {data.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            name="location"
            label="Location"
            value={inputData.location}
            onChange={handleChange}
            fullWidth
            margin="normal"
            error={!!errors.location}
            helperText={errors.location}
          />
        </Box>

        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          gap={2}
          marginTop={3}
        >
          <FormControlLabel
            control={
              <Switch
                checked={inputData.canSkip}
                name="canSkip"
                onChange={(e) => handleSwitch(e)}
                color="primary"
              />
            }
            label="Can Skip"
          />

          <FormControlLabel
            control={
              <Switch
                checked={inputData.canSend}
                name="canSend"
                onChange={(e) => handleSwitch(e)}
                color="primary"
              />
            }
            label="Can Send"
          />
        </Box>
      </Box>

      {/* Action buttons */}
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        gap={2}
        marginTop={3}
      >
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          fullWidth
        >
          Close
        </Button>
        <Button type="submit" variant="contained" color="primary" fullWidth>
          Save
        </Button>
      </Box>
    </form>
  );
};

export default WorkflowDetailForm;
